<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import SvSearchError from "@/components/product-listing/SvSearchError.vue";
import PromosNoResults from "@/components/product-listing/PromosNoResults.vue";
import PromosList from "@/components/product-listing/PromosList.vue";
import PromosSearchForm from "@/components/product-listing/PromosSearchForm.vue";
import ObserverComponent from "@/components/ObserverComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import PromosSortByVue from "@/components/product-listing/PromosSortBy.vue";
import { fetchPromosData } from "@/services/promos-ms-api";
import {
  formatPromosResponse,
  getSortFunction,
  filterInvalidProducts,
} from "@/utils/promos-utils";
import {
  getEnvVar,
  logException,
  pushDataLayerSearch,
  pushDataLayerClickPromoSortBy,
  LogExceptionCustomError,
} from "@/utils";
import type {
  Product,
  PromotionResponse,
} from "vacv-common/src/types/api/promotion/Api";
import {
  ProductExtended,
  PromosOutputParams,
  PromotionResponseExtended,
  SortIdOption,
  SortOrderOption,
} from "@/../types/promos-types";
import { VacvDropDownOption } from "@/../types/option";
import { DEFAULT_SORT_ID, DEFAULT_SORT_ORDER } from "@/constants";
import { usePromosSettingsStore } from "@/stores/promosSettingsStore";
import { pushDataLayerPromoFiltersEvent } from "@/utils/datalayer/promoFiltersDataLayer";
import { pushDataLayerNoResultsEvent } from "@/utils/datalayer/promoNoResultsDataLayer";
import { getCurrentLanguage, usePushVPVEvent, SpaType } from "vacv-common";
import useFlexPayMonthly from "@/utils/useFlexpayMonthly";

const lang = getCurrentLanguage();
const { t } = useI18n({ useScope: "global" });
const { pushingVPVEvent } = usePushVPVEvent(lang, SpaType.PromotionsSpa);
const promosSettingsStore = usePromosSettingsStore();
const pageSize = parseInt(getEnvVar("VITE_PLP_PAGINATION_SIZE"), 10) ?? 5;
const plpData = ref<PromotionResponseExtended>();
const numberFound = ref(0);
const searchError = ref<string>("");
const loading = ref(true);
const noResults = ref(false);
const page = ref<number>(1);
const sortId = ref<SortIdOption>(DEFAULT_SORT_ID);
const sortOrder = ref<SortOrderOption>(DEFAULT_SORT_ORDER);
const allProducts = ref<ProductExtended[]>([]);
const displayedProducts = ref<ProductExtended[]>([]);
const numPages = ref<number>(0);
const doNotTrigger = ref<boolean>(false);

const { upliftInited } = useFlexPayMonthly(displayedProducts);

const RETRY_TIMES =
  getEnvVar("VITE_MICROSERVICE_RETRY_TIMES") != null
    ? getEnvVar("VITE_MICROSERVICE_RETRY_TIMES")
    : 2;

onMounted(async () => {
  await loadPromosMicroserviceData();
  noResults.value && pushDataLayerNoResultsEvent(window.location.href);
});

const setProducts = (products: Product[], promosParams: PromosOutputParams) => {
  if (products && products.length !== 0) {
    const formattedPromos = formatPromosResponse({ products: products }, lang);

    const allProductsFiltered = filterInvalidProducts(
      formattedPromos.products ? formattedPromos.products : [],
      promosParams
    );
    allProductsFiltered.sort(getSortFunction(sortId.value, sortOrder.value));

    allProducts.value = allProductsFiltered;
    displayedProducts.value = allProductsFiltered.slice(0, pageSize);

    pushDataLayerSearch(displayedProducts.value, promosParams, 0);

    numberFound.value = allProductsFiltered.length;

    numPages.value = Math.ceil(allProductsFiltered.length / pageSize);

    noResults.value = numberFound.value === 0;
  } else {
    allProducts.value = [];
    displayedProducts.value = [];
    searchError.value = "";
    numberFound.value = 0;
    plpData.value = undefined;
    noResults.value = true;
  }
};

const handleResponse = (
  res: PromotionResponse,
  promosParams: PromosOutputParams
) => {
  setProducts(res?.products, promosParams);
};

const retryAfterErrorsHandle = (
  iteration: number,
  status: number,
  // eslint-disable-line
  errorMessage: string
) => {
  let doRetry = false;
  if (status == 401) {
    if (iteration == RETRY_TIMES) {
      logException(
        new Error(
          "HTTP 401: Invalid user token used to fetch data from promotion micro-service."
        )
      );
      alert(t("plp.messages.API401Error"));
      sessionLogout();
    } else {
      doRetry = true;
    }
  } else if (status == 403) {
    logException(
      new Error(
        "HTTP 403: User token is not authorized to access data from promotion micro-service."
      )
    );
    alert(t("plp.messages.API403Error"));
    sessionLogout();
  } else {
    logException({
      name: "Promotion search service error",
      message: errorMessage,
    } as LogExceptionCustomError);
    searchError.value = errorMessage;
    numberFound.value = 0;
    plpData.value = undefined;
  }
  return doRetry;
};

const loadPromosMicroserviceData = async () => {
  loading.value = true;
  noResults.value = false;
  searchError.value = "";

  try {
    for (let i = 1; i <= RETRY_TIMES; i++) {
      const tokenValue = await window.sso.getUserToken();
      if (tokenValue instanceof Error) {
        throw tokenValue;
      }

      if (!promosSettingsStore.isDurationInitialized) {
        await promosSettingsStore.fetchDurations();
      }

      const response: Response = await fetchPromosData(
        promosSettingsStore.promosParams,
        tokenValue
      );

      if (response.ok) {
        const jsonResponse = (await response.json()) as PromotionResponse;
        handleResponse(jsonResponse, promosSettingsStore.promosParams);
        // break out of retry loop after successful response
        break;
      } else {
        const success = await processServerError(response, i);
        if (success) {
          // if successful then exit
          break;
        }
        // if not successful then the request will be retried again
      }
    }
  } catch (e) {
    handleError(e);
  }
  loading.value = false; // disable loading spinner
  doNotTrigger.value = false;
};

const processServerError = async (
  response: Response,
  i: number
): Promise<boolean> => {
  let errorMessage = "";
  try {
    const jsonResponse = (await response.json()) as PromotionResponse;
    errorMessage = JSON.stringify(jsonResponse);
  } catch (e) {
    errorMessage = JSON.stringify(response);
  }
  const success = retryAfterErrorsHandle(i, response.status, errorMessage);
  return success;
};

const handleError = (e: unknown) => {
  if (e instanceof Error && e.message !== "") {
    logException(e);
    searchError.value = e.message;
  } else {
    logException(new Error(JSON.stringify(e)));
    searchError.value = JSON.stringify(e);
  }
  plpData.value = undefined;
};

const sessionLogout = () => {
  const linkTag: HTMLLinkElement | null = document.querySelector(
    ".login-state-logout a"
  );
  if (typeof window.location?.assign === "function") {
    if (linkTag) {
      window.location.assign(linkTag.href);
    } else {
      window.location.assign("/");
    }
  }
};

const clickSearch = async () => {
  await doSearch(DEFAULT_SORT_ID, DEFAULT_SORT_ORDER);
};

const onSearch = async () => {
  pushDataLayerPromoFiltersEvent(
    promosSettingsStore.promosParams.year_month,
    promosSettingsStore.promosParams.gateway,
    promosSettingsStore.promosParams.destination,
    promosSettingsStore.promosParams.duration,
    promosSettingsStore.promosParams.starRating
  );
  await clickSearch();
};

const doSearch = async (id: SortIdOption, order: SortOrderOption) => {
  sortId.value = id;
  sortOrder.value = order;

  page.value = 1;
  plpData.value = undefined;
  doNotTrigger.value = true;
  await loadPromosMicroserviceData();
  updateSearchFormParamsToUrl();
  // we need to send the noResults event after the url has been updated
  noResults.value && pushDataLayerNoResultsEvent(window.location.href);
};

const updateSearchFormParamsToUrl = () => {
  // Update URL search params so that when users refresh the page or come back to the page through browser history/back button
  // the search form can be properly initilized with previous selections
  const params = [];
  params.push(`tripMonth=${promosSettingsStore.promosParams.year_month}`);
  params.push(`leaveFrom=${promosSettingsStore.promosParams.gateway}`);
  params.push(`goTo=${promosSettingsStore.promosParams.destination}`);
  params.push(`duration=${promosSettingsStore.promosParams.duration}`);
  params.push("vacationType=PACKAGE");

  const uri = window.location.toString();
  let clean_uri = uri;
  if (uri.indexOf("?") > 0) {
    clean_uri = uri.substring(0, uri.indexOf("?"));
  }
  window.history.replaceState({}, document.title, clean_uri);
  let new_uri = clean_uri + "?";
  for (let i = 0; i < params.length; i++) {
    if (i == params.length - 1) {
      new_uri = new_uri + params[i];
    } else {
      new_uri = new_uri + params[i] + "&";
    }
  }
  window.history.replaceState({}, document.title, new_uri);
};

const loadMore = async (): Promise<void> => {
  if (page.value < numPages.value) {
    page.value++;
    const begin = displayedProducts.value.length;
    const end = Math.min(page.value * pageSize, allProducts.value.length);
    displayedProducts.value = allProducts.value.slice(0, end);

    pushDataLayerSearch(
      allProducts.value.slice(begin, end),
      promosSettingsStore.promosParams,
      begin
    );
  }
};

const scrollToTheBottom = (): void => {
  if (!doNotTrigger.value) {
    (async () => {
      await loadMore();
    })();
  }
};

const onChangeSort = (id: SortIdOption, order: SortOrderOption): void => {
  const lowhigh = order === "asc" ? "lowest" : "highest";
  if (id === "price") {
    pushDataLayerClickPromoSortBy(
      t(`plp.sorting.${lowhigh}-price`, 1, { locale: "en" })
    );
  } else if (id === "departure-date") {
    pushDataLayerClickPromoSortBy(
      t("plp.sorting.earliest-departure-date", 1, { locale: "en" })
    );
  } else if (id === "star-rating") {
    pushDataLayerClickPromoSortBy(
      t(`plp.sorting.${lowhigh}-star-rating`, 1, { locale: "en" })
    );
  }

  sortId.value = id;
  sortOrder.value = order;

  page.value = 1;
  plpData.value = undefined;
  updateSearchFormParamsToUrl();
  setProducts(allProducts.value, promosSettingsStore.promosParams);
};

const onDepartureMonthChanged = (newValue: VacvDropDownOption): void => {
  if (newValue) {
    promosSettingsStore.setMonth(newValue.value as string);
  }
};

const onLeaveFromChanged = (newValue: VacvDropDownOption): void => {
  if (newValue) {
    promosSettingsStore.setGateway(newValue.value as string);
  }
};

const onTravelToChanged = (newValue: VacvDropDownOption): void => {
  if (newValue) {
    promosSettingsStore.setDestination(newValue.value as string);
  }
};

const onDurationChanged = (newValue: VacvDropDownOption): void => {
  if (newValue) {
    promosSettingsStore.setDuration(newValue.value as string);
  }
};

const onStarRatingChanged = (newValue: VacvDropDownOption): void => {
  if (newValue) {
    promosSettingsStore.setStarRating(newValue.value as string);
  }
};

watch(loading, (newloadstatus: boolean) => {
  if (!newloadstatus) {
    pushingVPVEvent();
  }
});

// Expose methods and refs for unit tests
defineExpose({
  onSearch,
  onDepartureMonthChanged,
  onLeaveFromChanged,
  onTravelToChanged,
  onDurationChanged,
  scrollToTheBottom,
});
</script>
<template>
  <div class="vacv-promos">
    <article id="vacv-container" class="vacv-container-nopad">
      <promos-search-form
        :promos-params="promosSettingsStore.promosParams"
        :trip-departure-options="promosSettingsStore.monthOptions"
        :leave-from-options="promosSettingsStore.gatewaysOptions"
        :travel-to-options="promosSettingsStore.destinationOptions"
        :duration-options="promosSettingsStore.durationOptions"
        :star-rating-options="promosSettingsStore.starRatingOptions"
        :vacation-type="promosSettingsStore.vacationType"
        :is-durations-loading="promosSettingsStore.isDurationsLoading"
        @departure-month-change="onDepartureMonthChanged"
        @leave-from-change="onLeaveFromChanged"
        @travel-to-change="onTravelToChanged"
        @duration-change="onDurationChanged"
        @star-rating-change="onStarRatingChanged"
        @search="onSearch"
      />
      <loading-spinner v-if="loading" />
      <promos-no-results v-else-if="noResults" />
      <sv-search-error v-else-if="searchError" :error="searchError" />
      <main v-else class="vacv-main">
        <promos-sort-by-vue
          v-if="!noResults && !searchError"
          :id="sortId"
          :order="sortOrder"
          @change-sort="onChangeSort"
        />
        <promos-list
          v-if="!noResults && !searchError"
          :products="displayedProducts"
          :promos-params="promosSettingsStore.promosParams"
          :uplift-inited="upliftInited"
        />

        <observer-component @intersect="scrollToTheBottom" />
      </main>
    </article>
  </div>
</template>
<style scoped lang="scss">
.acv-logo {
  img {
    height: 40px;
  }
}
</style>
